import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import {
  STAFF_EMAIL_DEFAULT_DOMAIN,
  STAFF_ROLES,
  type StaffAuthMethod,
} from '../constants/staff';
import type { StaffListItem } from './StaffList';
import GoogleConsoleStaffDialog from './GoogleConsoleStaffDialog';

/** @より前のみ入力し、既定ドメインと結合する（Google 用） */
function buildStaffGmailEmail(localPart: string): string | null {
  const local = localPart.trim().toLowerCase();
  if (!local || local.includes('@')) return null;
  return `${local}@${STAFF_EMAIL_DEFAULT_DOMAIN}`;
}

function normalizeFullEmail(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  if (!t || !t.includes('@')) return null;
  return t;
}

const StaffNew: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [authMethod, setAuthMethod] = useState<StaffAuthMethod>('google');
  const [form, setForm] = useState({
    emailLocal: '',
    emailFull: '',
    display_name: '',
    role: '看護師' as (typeof STAFF_ROLES)[number],
    is_developer: false,
  });

  const handleSubmit = async () => {
    if (!form.display_name.trim()) {
      setError('必須項目を入力してください');
      return;
    }
    let email: string | null = null;
    if (authMethod === 'google') {
      email = buildStaffGmailEmail(form.emailLocal);
      if (!email) {
        setError('メールアドレス（@より前）を正しく入力してください');
        return;
      }
    } else {
      email = normalizeFullEmail(form.emailFull);
      if (!email) {
        setError('有効なメールアドレスを入力してください');
        return;
      }
    }

    setError('');
    setSubmitting(true);
    try {
      await apiClient.post<StaffListItem>('/api/staff', {
        email,
        display_name: form.display_name.trim(),
        role: form.role,
        is_developer: form.is_developer,
        auth_method: authMethod,
      });
      setRegisteredEmail(email);
      if (authMethod === 'google') {
        setGoogleDialogOpen(true);
      } else {
        navigate('/staff');
      }
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setError(String(e.response.data.error));
      } else {
        setError('登録に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <GoogleConsoleStaffDialog
        open={googleDialogOpen}
        mode="afterAdd"
        email={registeredEmail}
        onClose={() => {
          setGoogleDialogOpen(false);
          navigate('/staff');
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">職員を追加</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4, maxWidth: 560, mx: 'auto' }}>
        <TextField
          label="名前"
          fullWidth
          margin="normal"
          required
          value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
        />

        <FormControl sx={{ mt: 2 }} component="fieldset" variant="standard">
          <Typography component="legend" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            アカウント種別
          </Typography>
          <RadioGroup
            value={authMethod}
            onChange={(_, v) => setAuthMethod(v as StaffAuthMethod)}
          >
            <FormControlLabel value="google" control={<Radio />} label="Google" />
            <FormControlLabel value="email_password" control={<Radio />} label="それ以外" />
          </RadioGroup>
        </FormControl>

        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mt: 1.5,
            mb: 0.5,
            textAlign: 'left',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          {authMethod === 'google' ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              <strong>ログイン方法：</strong>
              職員本人はログイン画面の「Google でログイン」からサインインしてください。登録すると許可リストにメールアドレスが追加され、初回ログイン時にこのシステムと紐づきます。
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              <strong>ログイン方法：</strong>
              登録直後に、仮パスワードが記載された招待メールが届きます。職員本人はそのメールの案内に従い、ログイン画面でメールアドレスと仮パスワードを入力し、初回に新しいパスワードを設定してください。
            </Typography>
          )}
        </Alert>

        {authMethod === 'google' ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <TextField
                label="メールアドレス"
                type="text"
                fullWidth
                required
                autoComplete="username"
                value={form.emailLocal}
                onChange={(e) => setForm({ ...form, emailLocal: e.target.value })}
                inputProps={{ inputMode: 'email' }}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Typography
                component="span"
                variant="body1"
                color="text.secondary"
                sx={{ flexShrink: 0, lineHeight: 1 }}
              >
                @{STAFF_EMAIL_DEFAULT_DOMAIN}
              </Typography>
            </Box>
          </Box>
        ) : (
          <TextField
            label="メールアドレス"
            type="email"
            fullWidth
            margin="normal"
            required
            autoComplete="email"
            value={form.emailFull}
            onChange={(e) => setForm({ ...form, emailFull: e.target.value })}
          />
        )}

        <TextField
          label="役職"
          select
          fullWidth
          margin="normal"
          value={form.role}
          onChange={(e) =>
            setForm({
              ...form,
              role: e.target.value as (typeof STAFF_ROLES)[number],
            })
          }
        >
          {STAFF_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}
          control={
            <Switch
              checked={form.is_developer}
              onChange={(_, v) => setForm({ ...form, is_developer: v })}
            />
          }
          label={
            <Box>
              <Typography variant="body2">開発者アカウント</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                職員管理以外の院内データをサーバー側でマスクします
              </Typography>
            </Box>
          }
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
            登録
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default StaffNew;
